// Money is a DECIMAL STRING on the wire and INTEGER CENTS in arithmetic.
//
// The release gate and the reconcile flag both turn on one cent, and the Point
// Click Care CSV is byte-exact, so IEEE-754 drift across a summed set is a real
// hazard rather than a theoretical one. Nothing in this system adds two
// floating-point dollar amounts.

import { RECONCILE_TOLERANCE_CENTS } from './constants.js'

/** A money value as it crosses the API: `"1234.56"`, `"-120.00"`, `"0.00"`. */
export type MoneyString = string

const MONEY_PATTERN = /^-?\d+(\.\d+)?$/

/** True for a string this module will parse without throwing. */
export function isMoneyString(value: unknown): value is MoneyString {
  return typeof value === 'string' && MONEY_PATTERN.test(value.trim())
}

/**
 * Decimal string (or a JSON number) to integer cents.
 *
 * Null, undefined and blank return `null` — a missing total is a real state that
 * raises `MissingTotal`, not a zero. A malformed value THROWS: coercing garbage
 * to zero is how a taxed invoice silently exports short by exactly its tax.
 *
 * More than two decimal places are rounded half-away-from-zero, matching the
 * `numeric(14,2)` column and the export's own rounding.
 */
export function parseMoney(value: MoneyString | number | null | undefined): number | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new RangeError(`Not a money value: ${value}`)
    return centsFromNumber(value)
  }
  const trimmed = value.trim()
  if (trimmed === '') return null
  if (!MONEY_PATTERN.test(trimmed)) throw new RangeError(`Not a money value: ${JSON.stringify(value)}`)

  const negative = trimmed.startsWith('-')
  const unsigned = negative ? trimmed.slice(1) : trimmed
  const dot = unsigned.indexOf('.')
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot)
  const fraction = dot === -1 ? '' : unsigned.slice(dot + 1)

  // Two cent digits, then round on the third if there is one.
  const centDigits = (fraction + '00').slice(0, 2)
  let cents = Number(whole) * 100 + Number(centDigits)
  const nextDigit = fraction.charCodeAt(2)
  if (!Number.isNaN(nextDigit) && nextDigit >= 0x35) cents += 1 // '5' or above rounds up

  if (!Number.isSafeInteger(cents)) throw new RangeError(`Money value out of range: ${value}`)
  return negative ? -cents : cents
}

/**
 * A JS number of DOLLARS to integer cents, rounding half-away-from-zero on the
 * magnitude — EXACTLY, from the number's shortest decimal text
 * (`decimalFromNumber`), never by multiplying the float by 100 (code review
 * A-08 / B-12, 2026-10-03). `Math.round(1.005 * 100)` is `100`, because
 * `1.005 * 100` is `100.49999…` in binary; read as the decimal `"1.005"` it is
 * `101`, which is what the line totals' exact path (D-249) already gave. This
 * is how pass 1's header figures — tax, shipping, total, prior balance —
 * become money.
 */
export function centsFromNumber(dollars: number): number {
  if (!Number.isFinite(dollars)) throw new RangeError(`Not a money value: ${dollars}`)
  const cents = parseMoney(decimalFromNumber(dollars)) as number
  // Never produce -0: a result of zero is zero, so nothing can format as "-0.00".
  return cents === 0 ? 0 : cents
}

/**
 * Integer cents to the wire form: a plain decimal string with exactly two
 * decimals and NO thousand separators. `0` formats as `"0.00"`, never `"-0.00"`.
 */
export function formatMoney(cents: number): MoneyString {
  if (!Number.isInteger(cents)) throw new RangeError(`Cents must be an integer: ${cents}`)
  const normalized = cents === 0 ? 0 : cents
  const negative = normalized < 0
  const abs = Math.abs(normalized)
  const whole = Math.floor(abs / 100)
  const fraction = String(abs % 100).padStart(2, '0')
  return `${negative ? '-' : ''}${whole}.${fraction}`
}

/**
 * Integer cents to a grouped decimal string: two decimals WITH thousand
 * separators, e.g. `"2,630.36"`, `"-120.00"`.
 *
 * This is the Point Click Care CSV amount cell. It reproduces
 * `n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })`
 * exactly for every value a `numeric(14,2)` column can hold, without going near a
 * float. Because the output contains commas, the cell must stay quoted.
 */
export function formatMoneyGrouped(cents: number): string {
  const plain = formatMoney(cents)
  const negative = plain.startsWith('-')
  const unsigned = negative ? plain.slice(1) : plain
  const dot = unsigned.indexOf('.')
  const whole = unsigned.slice(0, dot)
  const fraction = unsigned.slice(dot)
  return `${negative ? '-' : ''}${group(whole)}${fraction}`
}

function group(digits: string): string {
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i]
  }
  return out
}

/**
 * US currency with a symbol, for the reminder digest. Mirrors the source's
 * `formatNumber(sum, 'C2', 'en-US')`: `"$1,234.56"`, and a negative renders as
 * `"-$1,234.56"`.
 */
export function formatCurrencyUSD(cents: number): string {
  const grouped = formatMoneyGrouped(cents)
  return grouped.startsWith('-') ? `-$${grouped.slice(1)}` : `$${grouped}`
}

// ---------------------------------------------------------------------------
// UNIT PRICE — the one amount that is NOT whole cents (owner, 2026-09-30 —
// `docs/decisions.md` D-249).
//
// A meter invoice charges $0.0049 a copy. Stored to the cent that price became
// $0.00 and the line no longer multiplied out to its bill amount. So a line's
// unit price is kept EXACTLY as printed, at whatever scale, and its Total is
// worked out from that exact price times the quantity, ROUNDED ONCE TO THE CENT
// ("at the end of the day we need to pay the Total on the invoice to the
// penny"). Every other amount — line totals, subtotal, tax, shipping, invoice
// total, everything in the Point Click Care CSV — stays whole cents. The unit
// price never reaches the export.
// ---------------------------------------------------------------------------

/** The most decimal places a unit price may carry. Far past any real invoice
    (a fifth-decimal price is already unusual); the cap is there to refuse abuse,
    not to round anything that is printed. */
export const PRICE_MAX_DECIMALS = 10

/** The most significant digits a unit price may carry. The app holds a price as
    a JS number, which is exact for any decimal of up to 15 significant digits,
    so a price inside this cap survives the round trip to the screen and back
    unchanged. */
export const PRICE_MAX_SIGNIFICANT_DIGITS = 15

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/

function significantDigits(unsigned: string): number {
  const digits = unsigned.replace('.', '').replace(/^0+/, '')
  return digits === '' ? 1 : digits.length
}

/**
 * True for a unit price the API accepts: a plain decimal string of at most
 * `PRICE_MAX_DECIMALS` places and `PRICE_MAX_SIGNIFICANT_DIGITS` significant
 * digits. A JSON number is not a price string.
 */
export function isPriceString(value: unknown): value is string {
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  if (!DECIMAL_PATTERN.test(trimmed)) return false
  const unsigned = trimmed.startsWith('-') ? trimmed.slice(1) : trimmed
  const dot = unsigned.indexOf('.')
  const decimals = dot === -1 ? 0 : unsigned.length - dot - 1
  return decimals <= PRICE_MAX_DECIMALS && significantDigits(unsigned) <= PRICE_MAX_SIGNIFICANT_DIGITS
}

/**
 * A price's canonical text: no leading zeros, AT LEAST two decimals, and no
 * trailing zeros past the second. `"0.00490"` -> `"0.0049"`, `"12.5"` ->
 * `"12.50"`, `"-0"` -> `"0.00"`. The digits that carry value are never changed,
 * so this is exact. Throws on anything that is not a decimal string.
 */
export function normalizePrice(value: string): string {
  const trimmed = value.trim()
  if (!DECIMAL_PATTERN.test(trimmed)) throw new RangeError(`Not a price: ${JSON.stringify(value)}`)
  const negative = trimmed.startsWith('-')
  const unsigned = negative ? trimmed.slice(1) : trimmed
  const dot = unsigned.indexOf('.')
  const whole = (dot === -1 ? unsigned : unsigned.slice(0, dot)).replace(/^0+(?=\d)/, '')
  let fraction = dot === -1 ? '' : unsigned.slice(dot + 1)
  fraction = fraction.replace(/0+$/, '')
  if (fraction.length < 2) fraction = fraction.padEnd(2, '0')
  const zero = /^0+$/.test(whole) && /^0+$/.test(fraction)
  return `${negative && !zero ? '-' : ''}${whole}.${fraction}`
}

/**
 * A JS number to its exact decimal text, with NO binary-float artefacts and no
 * exponent: the shortest string that reads back as the same number, which for
 * the model's `0.0049` is `"0.0049"` — never `0.00489999…`. `4.9e-7` becomes
 * `"0.00000049"`. This is how a price that arrives as a JSON number (pass 1's
 * answer) becomes a decimal string without passing through `toFixed`.
 */
export function decimalFromNumber(n: number): string {
  if (!Number.isFinite(n)) throw new RangeError(`Not a decimal value: ${n}`)
  const text = String(n)
  const match = /^(-?)(\d+)(?:\.(\d+))?e([+-]\d+)$/.exec(text)
  if (!match) return text === '-0' ? '0' : text
  const [, sign, intPart = '', fracPart = '', expText = '0'] = match
  const exp = Number(expText)
  const digits = intPart + fracPart
  // Position of the decimal point within `digits`, after the shift.
  const point = intPart.length + exp
  let out: string
  if (point <= 0) out = `0.${'0'.repeat(-point)}${digits}`
  else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length)
  else out = `${digits.slice(0, point)}.${digits.slice(point)}`
  return `${sign}${out}`
}

/**
 * A price read from a JSON number (pass 1), as the exact string to store:
 * `decimalFromNumber`, then — only if the model sent more than
 * `PRICE_MAX_DECIMALS` places, which no printed price has — rounded to that
 * many, half away from zero. Normalized by `normalizePrice`.
 */
export function priceFromNumber(n: number): string {
  const text = decimalFromNumber(n)
  const parsed = parseDecimal(text)
  if (parsed.scale <= PRICE_MAX_DECIMALS) return normalizePrice(text)
  const rounded = roundScaled(parsed.digits, parsed.scale - PRICE_MAX_DECIMALS)
  return normalizePrice(formatScaled(rounded, PRICE_MAX_DECIMALS))
}

interface ScaledDecimal {
  /** The value times 10^scale, exactly. */
  digits: bigint
  scale: number
}

function parseDecimal(value: string): ScaledDecimal {
  const trimmed = value.trim()
  if (!DECIMAL_PATTERN.test(trimmed)) throw new RangeError(`Not a decimal value: ${JSON.stringify(value)}`)
  const negative = trimmed.startsWith('-')
  const unsigned = negative ? trimmed.slice(1) : trimmed
  const dot = unsigned.indexOf('.')
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot)
  const fraction = dot === -1 ? '' : unsigned.slice(dot + 1)
  const digits = BigInt(whole + fraction)
  return { digits: negative ? -digits : digits, scale: fraction.length }
}

/** Divide by 10^places, rounding half away from zero — the ONE rounding step. */
function roundScaled(value: bigint, places: number): bigint {
  if (places <= 0) return value * 10n ** BigInt(-places)
  const divisor = 10n ** BigInt(places)
  const negative = value < 0n
  const magnitude = negative ? -value : value
  let quotient = magnitude / divisor
  if ((magnitude % divisor) * 2n >= divisor) quotient += 1n
  return negative ? -quotient : quotient
}

function formatScaled(value: bigint, scale: number): string {
  const negative = value < 0n
  const text = (negative ? -value : value).toString().padStart(scale + 1, '0')
  const whole = text.slice(0, text.length - scale)
  const fraction = text.slice(text.length - scale)
  return `${negative ? '-' : ''}${whole}${scale > 0 ? `.${fraction}` : ''}`
}

/** The decimal places a stored quantity keeps: `invoice_line_items.quantity`
    is `numeric(14,4)`. */
export const QUANTITY_MAX_DECIMALS = 4

/**
 * THE LARGEST FIGURES A COLUMN HOLDS (code review A-03, 2026-10-17). Every money
 * column but `unit_price` is `numeric(14,2)`, so a magnitude must stay under 10^12;
 * `quantity` is `numeric(14,4)`, under 10^10. Extraction refuses a figure past
 * either as that one document's contract failure — an account number read as an
 * amount used to overflow the column inside the upload's one write and fail every
 * document with it.
 */
export const MONEY_MAX_ABS = 1e12
export const QUANTITY_MAX_ABS = 1e10

/** How many decimal places a decimal string carries. Throws on anything else. */
export function decimalScale(value: string): number {
  return parseDecimal(value).scale
}

/**
 * A quantity's stored text: `numeric(14,4)`, exactly four decimals, the value
 * unchanged. Only for a quantity of at most `QUANTITY_MAX_DECIMALS` places —
 * one with more is the caller's to fold (see `pass1LineAmounts` in the
 * backend), never rounded here.
 */
export function quantityText(value: string): string {
  const parsed = parseDecimal(value)
  if (parsed.scale > QUANTITY_MAX_DECIMALS) {
    throw new RangeError(`Quantity has more than ${QUANTITY_MAX_DECIMALS} decimals: ${value}`)
  }
  return formatScaled(parsed.digits * 10n ** BigInt(QUANTITY_MAX_DECIMALS - parsed.scale), QUANTITY_MAX_DECIMALS)
}

/**
 * Round a decimal string half away from zero to `places` decimals. Used for
 * the one quantity that cannot be folded into its price (a quantity of more
 * than four places on a line whose price could not be read — it has no amount
 * to keep exact).
 */
export function roundDecimal(value: string, places: number): string {
  const parsed = parseDecimal(value)
  if (parsed.scale <= places) return formatScaled(parsed.digits * 10n ** BigInt(places - parsed.scale), places)
  return formatScaled(roundScaled(parsed.digits, parsed.scale - places), places)
}

/**
 * The exact product of two decimal strings, as a unit price
 * (`normalizePrice`). Exact unless the product carries more than
 * `PRICE_MAX_DECIMALS` places, when it is rounded there, half away from zero —
 * a ten-decimal unit price is already far past any printed one.
 */
export function priceProduct(a: string, b: string): string {
  const x = parseDecimal(a)
  const y = parseDecimal(b)
  const digits = x.digits * y.digits
  const scale = x.scale + y.scale
  if (scale <= PRICE_MAX_DECIMALS) return normalizePrice(formatScaled(digits, scale))
  return normalizePrice(formatScaled(roundScaled(digits, scale - PRICE_MAX_DECIMALS), PRICE_MAX_DECIMALS))
}

/**
 * A LINE'S TOTAL, IN INTEGER CENTS: the exact unit price times the exact
 * quantity, rounded ONCE, half away from zero, to the cent (D-4, D-10, D-249).
 * Worked in integers throughout — `0.0049 x 6066` is exactly `29.7234`, which
 * rounds to 2972 cents — so no binary-float product can land a hair under a
 * half cent and round the wrong way. Both arguments are decimal strings; this
 * throws on anything else, exactly as `parseMoney` does.
 */
export function lineTotalCents(price: string, quantity: string): number {
  const p = parseDecimal(price)
  const q = parseDecimal(quantity)
  const cents = roundScaled(p.digits * q.digits, p.scale + q.scale - 2)
  const n = Number(cents)
  if (!Number.isSafeInteger(n)) throw new RangeError(`Line total out of range: ${price} x ${quantity}`)
  return n === 0 ? 0 : n
}

/**
 * TWO DECIMAL STRINGS ARE THE SAME AMOUNT — compared by value, exactly, at any
 * scale: `"2.0000"` equals `"2"` and `"2.00"`, `"0.0049"` equals `"0.00490"`.
 * Both null is equal; one null is not. A column reads back at its own scale
 * (`numeric(14,4)` for a quantity, `numeric(14,2)` for money), so comparing the
 * TEXT of a stored value with a sent one recorded a change nobody made (code
 * review E-03). Text that is not a decimal is compared as text, so garbage is
 * never quietly equal to a number.
 */
export function decimalsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
  if (a === null || a === undefined || b === null || b === undefined) {
    return (a === null || a === undefined) && (b === null || b === undefined)
  }
  if (!DECIMAL_PATTERN.test(a.trim()) || !DECIMAL_PATTERN.test(b.trim())) return a === b
  const x = parseDecimal(a)
  const y = parseDecimal(b)
  const scale = Math.max(x.scale, y.scale)
  return x.digits * 10n ** BigInt(scale - x.scale) === y.digits * 10n ** BigInt(scale - y.scale)
}

/** Sum integer cents. Nulls are skipped, not treated as zero. */
export function sumCents(values: readonly (number | null | undefined)[]): number {
  let total = 0
  for (const v of values) {
    if (v === null || v === undefined) continue
    total += v
  }
  return total
}

/** |a - b| <= tolerance, in cents. */
export function withinTolerance(a: number, b: number, toleranceCents: number): boolean {
  return Math.abs(a - b) <= toleranceCents
}

/**
 * THE RECONCILE PREDICATE (owner, 2026-09-25 — `docs/decisions.md` D-151): a
 * difference of at most `RECONCILE_TOLERANCE_CENTS` (three cents), either way,
 * reconciles. Integer cents in, never dollars.
 *
 * The one rule every "does the money add up" check calls — the reconcile test,
 * the release gate, the flag's resolve condition and the export
 * guard — so none of them can apply a different standard
 * from another.
 */
export function reconciles(deltaCents: number): boolean {
  return Math.abs(deltaCents) <= RECONCILE_TOLERANCE_CENTS
}

/**
 * THE LARGEST ROW — the one a leftover cent lands on. `-1` when there are no
 * bases at all.
 *
 * The single LARGEST UNSIGNED base wins; a tie goes to the LOWER of the two
 * `sequences`, never to array order, so the answer does not depend on how a
 * caller happened to sort its rows. Extracted from `allocate` (which calls it)
 * so that the rounding remainder INSIDE an allocation and the odd cent absorbed
 * AFTER one (`docs/export.md` → The odd cent) can never pick different rows:
 * there is one rule and this is it.
 *
 * `bases` and `sequences` must be the same length and line up index for index.
 */
export function largestBaseIndex(
  bases: readonly number[],
  sequences: readonly number[],
): number {
  const n = bases.length
  if (n !== sequences.length) {
    throw new RangeError('largestBaseIndex: bases and sequences must be the same length')
  }
  if (n === 0) return -1

  let winner = 0
  for (let i = 1; i < n; i++) {
    const a = Math.abs(bases[i] as number)
    const b = Math.abs(bases[winner] as number)
    const largerBase = a > b
    const tiedLowerSequence = a === b && (sequences[i] as number) < (sequences[winner] as number)
    if (largerBase || tiedLowerSequence) winner = i
  }
  return winner
}

/**
 * Spread `amountCents` across `bases`, proportional to each base's magnitude, in
 * exact integer cents. This is the ratified sales-tax-spread rule — owned by
 * `exportBuilder.ts`, which is the one place under the current model that spreads
 * tax across GL codes when the Point Click Care CSV is built — and the general
 * proportional-allocation primitive besides. `bases` and `sequences` must be the
 * same length and line up index for index; the returned array does too.
 *
 * The algorithm, worked in whole cents throughout so two runs over the same
 * input produce the same answer to the cent:
 *
 *  1. Every base's SIGN IS DROPPED for the ratio (`B` is the sum of the
 *     UNSIGNED bases). `B === 0` — every base is zero, or there are none — means
 *     there is nothing to spread proportionally: every share is `0`.
 *     **This is the ONE case where the shares do not sum to `amountCents`**, and
 *     it is why `exportBuilder.ts` checks the spread separately from its
 *     total: dropping a whole tax figure silently is exactly the historical
 *     defect, so the caller must notice, not this primitive.
 *  2. Each base's raw share is `floor(|amountCents| * |base_i| / B)`, which
 *     under-allocates by a handful of cents (fewer than there are bases).
 *  3. ALL of that leftover goes to the group `largestBaseIndex` picks — the
 *     single LARGEST unsigned base, a tie to the LOWER of the two `sequences`,
 *     not array order — so the shares add up to `amountCents` exactly, nothing
 *     lost to rounding. That is the same row the odd cent is absorbed onto
 *     after the allocation (`docs/export.md` → The odd cent), by construction:
 *     both read the one function.
 *  4. Every share takes the SIGN of `amountCents` — a negative `amountCents`
 *     (a credit memo's tax) produces negative shares. A share of zero stays
 *     `0`, never `-0`, for the same reason `formatMoney` refuses `"-0.00"`.
 *
 * Because the signs are dropped in step 1, a MIXED-SIGN base list (a credit or
 * discount line beside ordinary ones) gives every group a share with the
 * amount's sign — a credit line's row moves TOWARDS zero rather than away from
 * it. That is deliberate: allocating on signed bases divides by a signed total
 * that can be zero, or near zero, while non-zero bases exist, which produces
 * shares wildly larger than the amount. Unsigned bases cannot. Either way the
 * shares sum to `amountCents` exactly, so the export guard holds.
 *
 * Worked example (the one this function is pinned against): spreading 7.31 of
 * tax over bases 37.00 / 37.00 / 26.00 gives shares `[271, 270, 190]` — the two
 * tied 37.00 bases each floor to 270, the 26.00 base floors to 190, and the one
 * leftover cent goes to the lower-sequenced of the tied pair.
 */
export function allocate(
  amountCents: number,
  bases: readonly number[],
  sequences: readonly number[],
): number[] {
  const n = bases.length
  if (n !== sequences.length) {
    throw new RangeError('allocate: bases and sequences must be the same length')
  }
  if (n === 0) return []

  const sign = amountCents < 0 ? -1 : 1
  const amount = Math.abs(amountCents)
  const absBases = bases.map((b) => Math.abs(b))
  const total = sumCents(absBases)
  if (total === 0) return absBases.map(() => 0)

  const shares = absBases.map((b) => Math.floor((amount * b) / total))
  const leftover = amount - sumCents(shares)

  const winner = largestBaseIndex(absBases, sequences)
  shares[winner] = (shares[winner] as number) + leftover

  // `0 * -1` is `-0` in JavaScript. Nothing downstream would misprint it — every
  // formatter here normalizes — but a money primitive that can hand back `-0` is
  // one an assertion can pass or fail on the sign of nothing, so it does not.
  return shares.map((cents) => (cents === 0 ? 0 : cents * sign))
}
